"use client"

import { useEffect, useMemo, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { motion } from "framer-motion"
import { ChevronRight } from "lucide-react"
import { useDispatch, useSelector } from "react-redux"

import Topnav from "@/components/topnav"
import TextRegularM from "@/components/text/text-regular-m"
import TitleL from "@/components/text/title-l"
import PlayButton from "@/components/buttons/PlayButton"

import PlaylistDetails from "@/app/pages/playlistPage/components/playlistHeader"
import PlaylistTrack from "@/app/pages/playlistPage/components/playlistTracklist"
import PlaylistFooter from "@/app/pages/playlistPage/components/playlistCreditos"
import PlaylistCardS from "@/app/pages/homePage/components/playlists/cardPlaylist/card-playlist"

import { PLAYLIST } from "@/data"
import { calcularDuracionPlaylist } from "@/core/utils/utils"
import { changeTrack } from "@/core/actions"

type Params = {
  link?: string
}

type PlaylistData = {
  title: string
  type: string
  link: string
  playlistBg: string
  playlistData: any[]
  imgUrl: string
  artist: string
  artistImgUrl: string
  artistBio: string
  playlistImages?: string[]
}

const pageVariants = {
  initial: { opacity: 0, y: 10 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: { duration: 0.3 },
  },
}

export default function PodcastPage() {
  const dispatch = useDispatch()
  const { link } = useParams<Params>()

  const trackData = useSelector((state: any) => state.trackData)

  const [podcastIndex, setPodcastIndex] = useState<number | undefined>()
  const [isThisPlay, setIsThisPlay] = useState(false)

  useEffect(() => {
    setIsThisPlay(podcastIndex === trackData.trackKey?.[0])
  }, [podcastIndex, trackData.trackKey])

  const podcast = useMemo(() => {
    return PLAYLIST.find(
      (item: any) => item.link === link && item.type === "podcast"
    ) as PlaylistData | undefined
  }, [link])

  useEffect(() => {
    if (!podcast) return

    document.documentElement.style.setProperty(
      "--hover-home-bg",
      podcast.playlistBg
    )

    setPodcastIndex(PLAYLIST.indexOf(podcast))
  }, [podcast])

  const shuffleArray = <T,>(array: T[]): T[] =>
    [...array].sort(() => Math.random() - 0.5)

  const handleSongClick = (item: any, song: any) => {
    const playlistIndex = PLAYLIST.indexOf(item)
    const songIndex = item.playlistData.findIndex(
      (s: any) => s.index === song.index
    )

    if (playlistIndex === -1 || songIndex === -1) return

    dispatch(changeTrack([playlistIndex, songIndex]))
  }

  const shuffledPodcasts = useMemo(() => {
    return shuffleArray(
      PLAYLIST.filter(
        (item: any) => item.type === "podcast" && item.link !== link
      ).slice(0, 10)
    )
  }, [link])

  if (!podcast) return null

  const duracionTotal = calcularDuracionPlaylist(podcast.playlistData)

  return (
    <div className="relative w-full min-h-screen text-white bg-black overflow-x-hidden">
      <Topnav />

      {/* BACKGROUNDS */}
      <div className="absolute top-0 left-0 right-0 h-[340px] -z-10 bg-gradient-to-b from-transparent to-black/50"
        style={{ backgroundColor: "var(--hover-home-bg)" }}
      />

      <div className="absolute top-[340px] left-0 right-0 h-[240px] -z-20 bg-gradient-to-b from-black/50 to-black"
        style={{ backgroundColor: "var(--hover-home-bg)" }}
      />

      <div className="fixed inset-0 -z-30 bg-[var(--main-background)]" />

      <motion.div
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="pb-32"
      >
        {/* DETAILS */}
        <PlaylistDetails
          data={podcast}
          duracionTotal={duracionTotal}
        />

        {/* PLAY BUTTON */}
        <div className="flex justify-center py-6">
          <div
            onClick={() =>
              handleSongClick(podcast, podcast.playlistData[0])
            }
            className="w-16 h-16 rounded-full bg-[#066eff] flex items-center justify-center active:scale-95 transition"
          >
            <PlayButton />
          </div>
        </div>

        {/* HEADER */}
        <div className="grid grid-cols-[2.5rem_1fr_1.5rem_3.5rem] px-4 mb-2 text-zinc-400 text-sm">
          <TextRegularM className="text-center">#</TextRegularM>
          <TextRegularM className="text-left">Título</TextRegularM>
          <TextRegularM className="text-center">↓</TextRegularM>
          <TextRegularM className="text-center">⏱</TextRegularM>
        </div>

        {/* EPISODES */}
        <div className="grid gap-2 px-4">
          {podcast.playlistData.map((episode: any) => (
            <div
              key={episode.index}
              onClick={() => handleSongClick(podcast, episode)}
              className="rounded-xl bg-white/5 shadow-inner border border-white/10"
            >
              <PlaylistTrack
                data={{
                  listType: podcast.type,
                  song: episode,
                }}
              />
            </div>
          ))}
        </div>

        {/* SIMILARES */}
        <section className="mt-10">
          <Link
            to="/library/podcasts"
            className="flex items-center gap-1 px-4 mb-4 w-fit active:scale-95 transition"
          >
            <TitleL>Podcasts Similares</TitleL>
            <ChevronRight size={18} />
          </Link>

          <div className="flex gap-4 overflow-x-auto px-4 pb-6">
            {shuffledPodcasts.map((item: any, index: number) => (
              <div
                key={item.title}
                style={{ animationDelay: `${index * 0.15}s` }}
                className="fade-in"
              >
                <PlaylistCardS data={item} />
              </div>
            ))}
          </div>
        </section>

        {/* FOOTER */}
        <PlaylistFooter data={podcast} />
      </motion.div>
    </div>
  )
}